import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material'
import MerchantToolShell, { shellStyles } from './MerchantToolShell'
import { useAuth } from '../../contexts/AuthContext'
import { useLanguage } from '../../contexts/LanguageContext'
import api from '../../services/api'

const accessTypeOptions = [
    { value: 'general', labelEn: 'General Admission', labelFr: 'Admission generale' },
    { value: 'seat', labelEn: 'Assigned Seat', labelFr: 'Places attribuees' },
    { value: 'table', labelEn: 'Table Booking', labelFr: 'Reservation table' },
]

const bannerFileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
        const image = new Image()
        image.onload = () => {
            const maxWidth = 1280
            const maxHeight = 560
            const scale = Math.min(1, maxWidth / image.width, maxHeight / image.height)
            const canvas = document.createElement('canvas')
            canvas.width = Math.max(1, Math.round(image.width * scale))
            canvas.height = Math.max(1, Math.round(image.height * scale))
            const context = canvas.getContext('2d')
            context.fillStyle = '#120c2d'
            context.fillRect(0, 0, canvas.width, canvas.height)
            context.drawImage(image, 0, 0, canvas.width, canvas.height)
            resolve(canvas.toDataURL('image/jpeg', 0.78).split(',')[1] || '')
        }
        image.onerror = reject
        image.src = String(reader.result || '')
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
})

export default function MerchantCreateEvent() {
    const { user } = useAuth()
    const { lang } = useLanguage()
    const navigate = useNavigate()
    const location = useLocation()
    const isFr = lang === 'fr'
    const initialCreatedEvent =
        location.state && typeof location.state === 'object'
            ? location.state.createdEvent
            : null
    const defaultCountry = user?.business_country || user?.businessCountry || user?.country || ''
    const bannerInputRef = useRef(null)
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [city, setCity] = useState('')
    const [address, setAddress] = useState('')
    const [country, setCountry] = useState(defaultCountry)
    const [eventDate, setEventDate] = useState('')
    const [eventTime, setEventTime] = useState('')
    const [accessType, setAccessType] = useState('general')
    const [price, setPrice] = useState('')
    const [ticketCount, setTicketCount] = useState('')
    const [bannerFile, setBannerFile] = useState(null)
    const [bannerName, setBannerName] = useState('')
    const [bannerPreview, setBannerPreview] = useState('')
    const [createdEvent, setCreatedEvent] = useState(initialCreatedEvent)
    const [existingEvents, setExistingEvents] = useState([])
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState('')

    const currency = useMemo(() => {
        const key = String(country || defaultCountry || '').trim().toUpperCase()
        return user?.default_currency || user?.business_currency || user?.baseCurrency || ({
            US: 'USD',
            USA: 'USD',
            CA: 'CAD',
            GB: 'GBP',
            UK: 'GBP',
            AU: 'AUD',
            FR: 'EUR',
            SL: 'SLE',
            LR: 'LRD',
            GN: 'GNF',
            GM: 'GMD',
        }[key] || 'USD')
    }, [country, defaultCountry, user])

    useEffect(() => {
        let active = true
        api.get('/merchant/events')
            .then((response) => {
                if (!active) return
                setExistingEvents(Array.isArray(response.data?.data) ? response.data.data.slice(0, 3) : [])
            })
            .catch(() => {
                if (active) setExistingEvents([])
            })
        return () => {
            active = false
        }
    }, [])

    const accessTypeHelper = useMemo(() => {
        if (accessType === 'seat') {
            return isFr
                ? 'Les clients choisissent des sieges ou des sections definies.'
                : 'Guests buy seat-based tickets with sections or assigned rows.'
        }
        if (accessType === 'table') {
            return isFr
                ? 'Les clients reservent des tables ou des espaces groupe.'
                : 'Guests book tables or grouped hospitality access.'
        }
        return isFr
            ? 'Les clients achetent des billets standards sans siege ni table.'
            : 'Guests buy standard tickets with no fixed seat or table assignment.'
    }, [accessType, isFr])

    const handleBannerPick = (event) => {
        const file = event.target.files?.[0]
        if (!file) return
        setBannerFile(file)
        setBannerName(file.name)
        try {
            setBannerPreview(URL.createObjectURL(file))
        } catch {
            setBannerPreview('')
        }
    }

    const openBannerPicker = () => {
        bannerInputRef.current?.click()
    }

    const handleCreate = async () => {
        const trimmedTitle = title.trim()
        const trimmedCity = city.trim()
        const trimmedCountry = country.trim()
        const numericPrice = Number(price || 0)
        const numericTicketCount = Number(ticketCount || 0)

        if (!trimmedTitle) {
            setError(isFr ? 'Ajoutez un titre evenement.' : 'Add an event title.')
            return
        }
        if (!trimmedCity) {
            setError(isFr ? 'Ajoutez le lieu ou la ville.' : 'Add the venue or city.')
            return
        }
        if (!trimmedCountry) {
            setError(isFr ? 'Ajoutez le pays.' : 'Add the country.')
            return
        }
        if (!eventDate || !eventTime) {
            setError(isFr ? 'Ajoutez la date et l heure.' : 'Add the date and time.')
            return
        }
        if (!(numericPrice > 0)) {
            setError(isFr ? 'Ajoutez un prix valide.' : 'Add a valid price.')
            return
        }
        if (!(numericTicketCount > 0)) {
            setError(isFr ? 'Ajoutez le total billets.' : 'Add total tickets.')
            return
        }

        setIsSaving(true)
        try {
            const startsAt = new Date(`${eventDate}T${eventTime || '00:00'}`)
            const payload = {
                title: trimmedTitle,
                description: description.trim(),
                venue_name: trimmedCity,
                venue_address: address.trim(),
                country: trimmedCountry,
                starts_at: startsAt.toISOString(),
                currency,
                access_type: accessType,
                event_type: accessType === 'general' ? 'general' : 'reserved',
                price: numericPrice,
                total_tickets: numericTicketCount,
            }
            if (bannerFile) {
                payload.banner_image_data = await bannerFileToBase64(bannerFile)
                payload.banner_image_name = bannerFile.name
            }
            const response = await api.post('/merchant/events', payload)
            const record = response.data?.event
            setCreatedEvent(record)
            setExistingEvents((current) => [record, ...current.filter((item) => item.id !== record?.id)].slice(0, 3))
            setError('')
            if (record?.id) {
                window.setTimeout(() => {
                    navigate('/merchant/events', {
                        replace: true,
                        state: { createdEvent: record },
                    })
                }, 450)
            }
        } catch (err) {
            setError(err?.response?.data?.message || (isFr ? 'Impossible de creer evenement.' : 'Could not create the event.'))
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <MerchantToolShell
            eyebrow={isFr ? 'Billetterie' : 'Ticketing'}
            title={isFr ? 'Creer evenement' : 'Create Event'}
            subtitle={
                isFr
                    ? 'Utilisez le meme flux clair que sur mobile pour lancer votre evenement, ajouter le lieu, le type de billet et la capacite.'
                    : 'Use the same clean flow as mobile to launch your event, add the venue, ticket type, and selling capacity.'
            }
            chips={[
                'General',
                isFr ? 'VIP ou table' : 'VIP or table',
                isFr ? 'Pret a vendre' : 'Ready to sell',
            ]}
            primaryAction={{
                label: isSaving ? (isFr ? 'Creation...' : 'Creating...') : (isFr ? 'Creer evenement' : 'Create Event'),
                onClick: handleCreate,
            }}
            left={
                <Stack spacing={2.2}>
                    {error && <Alert severity="error">{error}</Alert>}
                    {createdEvent && (
                        <Alert severity="success">
                            {isFr
                                ? `Evenement cree: ${createdEvent.title}. Reference ${String(createdEvent.id || createdEvent.reference || '').toUpperCase()}.`
                                : `Event created: ${createdEvent.title}. Reference ${String(createdEvent.id || createdEvent.reference || '').toUpperCase()}.`}
                        </Alert>
                    )}

                    <Box>
                        <Typography sx={{ fontWeight: 800, mb: 0.6 }}>
                            {isFr ? 'Banniere evenement' : 'Event Banner'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 1.5 }}>
                            {isFr
                                ? 'Taille recommandee: 1600 x 700 px'
                                : 'Recommended size: 1600 x 700 px'}
                        </Typography>
                        <input
                            ref={bannerInputRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={handleBannerPick}
                        />
                        <Box
                            onClick={openBannerPicker}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault()
                                    openBannerPicker()
                                }
                            }}
                            sx={{
                                ...shellStyles.softPanel,
                                minHeight: 180,
                                display: 'grid',
                                placeItems: 'center',
                                textAlign: 'center',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                backgroundImage: bannerPreview ? `linear-gradient(rgba(8,6,27,0.12), rgba(8,6,27,0.12)), url(${bannerPreview})` : undefined,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                '&:hover': {
                                    borderColor: 'rgba(255,138,0,0.5)',
                                },
                            }}
                        >
                            <Stack spacing={1} sx={{ px: 2 }}>
                                <Typography sx={{ fontWeight: 800, color: bannerPreview ? '#FFFFFF' : 'inherit' }}>
                                    {bannerPreview
                                        ? (isFr ? 'Cliquer pour changer la banniere' : 'Click to change banner')
                                        : (isFr ? 'Touchez pour ajouter la banniere' : 'Tap to add event banner')}
                                </Typography>
                                <Typography sx={{ color: bannerPreview ? 'rgba(255,255,255,0.88)' : 'text.secondary', fontSize: 13 }}>
                                    {bannerName
                                        ? bannerName
                                        : isFr
                                            ? 'Utilisez une image large, idealement 1600 x 700 px.'
                                            : 'Use a wide cover image, ideally 1600 x 700 px.'}
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>

                    <Typography sx={{ fontWeight: 800, pt: 0.5 }}>
                        {isFr ? 'Infos evenement' : 'Event Info'}
                    </Typography>
                    <TextField
                        label={isFr ? 'Titre evenement' : 'Event Title'}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />
                    <TextField
                        label={isFr ? 'Description' : 'Description'}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        minRows={3}
                    />

                    <Typography sx={{ fontWeight: 800, pt: 0.5 }}>
                        {isFr ? 'Lieu' : 'Location'}
                    </Typography>
                    <TextField
                        label={isFr ? 'Lieu / ville' : 'Venue / City'}
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                    />
                    <TextField
                        label={isFr ? 'Adresse' : 'Address'}
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                    />
                    <TextField
                        label={isFr ? 'Pays' : 'Country'}
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                    />

                    <Typography sx={{ fontWeight: 800, pt: 0.5 }}>
                        {isFr ? 'Date et heure' : 'Date & Time'}
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <TextField
                            type="date"
                            label={isFr ? 'Date' : 'Date'}
                            value={eventDate}
                            onChange={(e) => setEventDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            type="time"
                            label={isFr ? 'Heure' : 'Time'}
                            value={eventTime}
                            onChange={(e) => setEventTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                    </Stack>

                    <Typography sx={{ fontWeight: 800, pt: 0.5 }}>
                        {isFr ? 'Billets' : 'Tickets'}
                    </Typography>
                    <TextField
                        label={isFr ? 'Type acces' : 'Access Type'}
                        value={accessType}
                        onChange={(e) => setAccessType(e.target.value)}
                        select
                        SelectProps={{ native: true }}
                    >
                        {accessTypeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {isFr ? option.labelFr : option.labelEn}
                            </option>
                        ))}
                    </TextField>
                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                            {accessTypeHelper}
                        </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <TextField
                            label={isFr ? 'Prix' : 'Price'}
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label={isFr ? 'Total billets' : 'Total Tickets'}
                            value={ticketCount}
                            onChange={(e) => setTicketCount(e.target.value)}
                            fullWidth
                        />
                    </Stack>
                    <Button
                        fullWidth
                        variant="contained"
                        disabled={isSaving}
                        onClick={handleCreate}
                        sx={{
                            bgcolor: '#FF8A00',
                            color: '#0B0620',
                            borderRadius: 999,
                            py: 1.35,
                            fontWeight: 900,
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#FF9F2A' },
                        }}
                    >
                        {isSaving ? (isFr ? 'Creation...' : 'Creating...') : (isFr ? 'Creer evenement' : 'Create Event')}
                    </Button>
                </Stack>
            }
            right={
                <Stack spacing={2}>
                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 0.8 }}>
                            {isFr ? 'Avant publication' : 'Before launch'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                            {isFr
                                ? 'Le web peut lancer rapidement un evenement. Les details plus riches peuvent ensuite rester sur mobile.'
                                : 'Web can launch the event quickly. The richer setup can still stay on mobile after creation.'}
                        </Typography>
                    </Box>
                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 0.8 }}>
                            {isFr ? 'Billets et sections' : 'Tickets and sections'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                            {isFr
                                ? 'General, VIP, VVIP, Couple, siege ou table doivent rester alignes avec le flux mobile.'
                                : 'General, VIP, VVIP, Couple, seat, or table setup should stay aligned with the mobile ticket flow.'}
                        </Typography>
                    </Box>
                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 0.8 }}>
                            {isFr ? 'Promotion' : 'Promotion'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                            {isFr
                                ? 'Une fois cree, l evenement doit se partager proprement vers diaspora et local.'
                                : 'Once created, the event should share cleanly to both diaspora and local audiences.'}
                        </Typography>
                    </Box>
                </Stack>
            }
            bottom={
                <Stack spacing={2}>
                    <Typography sx={{ fontWeight: 800 }}>
                        {isFr ? 'Evenements recents' : 'Recent Events'}
                    </Typography>
                    {createdEvent ? (
                        <Stack sx={shellStyles.softPanel} spacing={1}>
                            <Typography sx={{ fontWeight: 800 }}>{createdEvent.title}</Typography>
                            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                {isFr
                                ? `${createdEvent.venue_name || createdEvent.city || ''} | ${createdEvent.access_type || accessType} | Prix ${createdEvent.currency || currency} ${createdEvent.price || price} | Billets ${createdEvent.total_tickets || ticketCount}`
                                    : `${createdEvent.venue_name || createdEvent.city || ''} | ${createdEvent.access_type || accessType} | Price ${createdEvent.currency || currency} ${createdEvent.price || price} | Tickets ${createdEvent.total_tickets || ticketCount}`}
                            </Typography>
                        </Stack>
                    ) : existingEvents.length > 0 ? (
                        existingEvents.map((event) => (
                            <Stack key={event.id} sx={shellStyles.softPanel} spacing={0.8}>
                                <Typography sx={{ fontWeight: 800 }}>{event.title}</Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    {isFr
                                        ? `${event.venue_name || event.city || ''} | ${event.access_type || event.accessType || ''} | Prix ${event.currency || currency} ${event.price}`
                                        : `${event.venue_name || event.city || ''} | ${event.access_type || event.accessType || ''} | Price ${event.currency || currency} ${event.price}`}
                                </Typography>
                            </Stack>
                        ))
                    ) : (
                        <Typography sx={{ color: 'text.secondary' }}>
                            {isFr
                                ? 'Aucun evenement web cree pour le moment.'
                                : 'No web event created yet.'}
                        </Typography>
                    )}
                </Stack>
            }
        />
    )
}
